import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import { Close } from '@material-ui/icons';
import { Accordion, AccordionSummary } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReactSVG from 'react-svg';
import * as actions from '~/store/actions';
import { RootState } from '~/store/config';
import * as Loaders from '~/components/Loaders';
import { SmsTemplate } from '~/types/accountInfo';
import { CircularLoader } from '~/components/CallHistory/styles';
import { ChannelTypeEnum } from '~/enums/conversations';
import {
  MessagesIcon,
  SMSIcon,
  WhatsappIcon,
  Icon10DLC,
} from '~/assets/svgIcons';
import WhatsappPreview from '../WhatsappPreview';
import {
  Body,
  Button,
  Container,
  Header,
  Loader,
  RowItem,
  TemplateContent,
  Usage,
  TemplateName,
  TextField,
  NoTemplateFound,
  AccordionDetails,
} from './styles';

const usageList: any[] = [
  {
    id: 0,
    icon: MessagesIcon,
    usage: 'default',
  },
  {
    id: 1,

    icon: SMSIcon,
    usage: 'sms',
  },
  {
    id: 2,
    icon: WhatsappIcon,
    usage: 'whatsapp',
  },
  {
    id: 3,
    icon: Icon10DLC,
    usage: '10dlc',
  },
  {
    id: 3,
    icon: WhatsappIcon,
    usage: 'conreg',
  },
];

type TemplatesProps = {
  onClose: () => void;
  /**
   * Emitted when the user clicks a template
   * @param template returns the selected template
   */
  onTemplateChange: (template: SmsTemplate) => void;
  channelType: ChannelTypeEnum;
};

const Row = ({
  item: template,
  onTemplateChange,
  expanded,
  onExpandedChange,
}: {
  item: SmsTemplate;
  onTemplateChange: any;
  expanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
}) => {
  const { id, name, usage, messageText } = template;

  const isWhatsAppTemplate = usage === 'whatsapp';

  const usageIcon =
    usageList.find(item => item.usage === usage).icon || usageList[0].icon;

  let templateInfo;

  if (isWhatsAppTemplate) {
    // @ts-ignore
    const buttons = template?.config.components.find(
      (component: { type: string }) => component.type === 'BUTTONS'
    )?.buttons;
    // @ts-ignore
    const mediaInfo = template?.config.components.find(
      (component: { type: string }) => component.type === 'HEADER'
    )?.media;

    templateInfo = {
      bodyText: messageText,
      ...(mediaInfo && {
        mediaUrl: mediaInfo.url,
        mediaType: mediaInfo.type,
        templateType: 'media',
      }),
      ...(buttons?.length && {
        buttons,
      }),
    };
  }

  return (
    <RowItem
      data-test-id={`template-item-${id}`}
      data-template-id={id}
      data-template-type={templateInfo?.templateType}
      data-template-usage={usage}>
      <Accordion
        square
        key={id}
        style={{ width: '100%' }}
        expanded={expanded}
        onChange={(_event, isExpanded) => onExpandedChange(isExpanded)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel${id}a-content`}
          id={`panel${id}a-header`}>
          <Usage>
            <ReactSVG src={usageIcon} />
          </Usage>
          <TemplateName
            data-test-id={`template-name-${id}`}
            title={name}
            onClick={() => onTemplateChange(template)}>
            {name}
          </TemplateName>
        </AccordionSummary>
        <AccordionDetails>
          {expanded && (
            <TemplateContent onClick={() => onTemplateChange(template)}>
              {isWhatsAppTemplate ? (
                // @ts-ignore
                <WhatsappPreview {...templateInfo} />
              ) : (
                messageText
              )}
            </TemplateContent>
          )}
        </AccordionDetails>
      </Accordion>
    </RowItem>
  );
};

export default function Templates({
  onClose,
  onTemplateChange,
  channelType,
}: TemplatesProps) {
  // HOOK's
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // STATE's
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedTemplateId, setExpandedTemplateId] = useState<
    SmsTemplate['id'] | null
  >(null);

  // SELECTOR's
  const { smsTemplates } = useSelector((state: RootState) => state.accountInfo);

  // Filter templates based on channel type
  const channelTemplates =
    channelType === ChannelTypeEnum.WHATSAPP
      ? smsTemplates?.filter(template => template.usage === 'whatsapp') || []
      : smsTemplates?.filter(template => template.usage !== 'whatsapp') || [];

  // CONST's
  const perPage = 45;
  const filteredTemplates = channelTemplates.filter(({ name }) =>
    name.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  useEffect(() => {
    if (
      expandedTemplateId !== null &&
      !filteredTemplates.some(template => template.id === expandedTemplateId)
    ) {
      setExpandedTemplateId(null);
    }
  }, [expandedTemplateId, filteredTemplates]);

  const handleEndReached = useCallback(() => {
    const newPage = page + 1;
    dispatch(actions.fetchSmsTemplates(newPage, perPage));
    setPage(newPage);
  }, [dispatch, page]);

  const handleStartReached = () => {
    const newPage = 1;
    dispatch(actions.fetchSmsTemplates(newPage, perPage));
    setPage(newPage);
  };

  // EFFECT's
  useEffect(() => {
    dispatch(actions.fetchSmsTemplates(page, perPage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (smsTemplates.length === 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [smsTemplates]);

  return (
    <Container data-test-id="templates-panel">
      <Header>
        <span>{t('screens.conversation.selectTemplate')}</span>
        <Button data-test-id="close-templates" onClick={onClose}>
          <Close color="inherit" />
        </Button>
      </Header>
      <TextField
        id="search-template"
        data-test-id="search-template"
        placeholder={t('screens.conversation.searchTemplateNames')}
        value={searchQuery}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setSearchQuery(event.target.value)
        }
      />
      <Body data-test-id="templates-list">
        {loading ? (
          <Loader>
            <Loaders.List numRows={10} />
          </Loader>
        ) : (
          <Virtuoso
            className="template-list-virtuoso"
            item={index => (
              <Row
                item={filteredTemplates[index]}
                onTemplateChange={onTemplateChange}
                expanded={filteredTemplates[index]?.id === expandedTemplateId}
                onExpandedChange={isExpanded =>
                  setExpandedTemplateId(
                    isExpanded ? filteredTemplates[index]?.id ?? null : null
                  )
                }
              />
            )}
            totalCount={filteredTemplates.length}
            style={{ flex: 1 }}
            startReached={handleStartReached}
            endReached={handleEndReached}
            footer={() => (
              <>{loading && <CircularLoader size={30} thickness={2} />}</>
            )}
            emptyComponent={() => (
              <NoTemplateFound>
                {t('screens.conversation.noTemplatesFound')}
              </NoTemplateFound>
            )}
          />
        )}
      </Body>
    </Container>
  );
}
